import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { ChangePasswordDrawerComponent } from './shared/components/change-password-drawer/change-password-drawer.component';
import { ChangePasswordService } from './shared/services/change-password.service';
import { ImportDataPanelComponent } from './shared/components/import-data-panel/import-data-panel.component';
import { KeyboardNavigationService } from './shared/services/keyboard-navigation.service';
import { GlobalShortcutsService } from './shared/services/global-shortcuts.service';
import { ModuleDropdownShortcutService } from './shared/services/module-dropdown-shortcut.service';
import { CommandPaletteService } from './layout/common/command-palette/core/command-palette.service';
import { WorkspaceTabsService } from './layout/common/workspace-tabs/workspace-tabs.service';
import { WorkspaceDialogService } from './layout/common/workspace-tabs/overlay/workspace-dialog.service';
import { AppDialogPositionsService } from './layout/common/workspace-tabs/overlay/app-dialog-positions.service';
import { MatDialog } from '@angular/material/dialog';
import { attachDialogFocusManagement, hasVisibleDialogOverlay } from './shared/utils/dialog-keyboard-keys';

/** Pending second key for transaction shortcuts (Alt+P,R etc.) and list shortcuts (Alt+I/C/D+L) */
type PendingTransactionShortcut = 'altP' | 'altS' | 'altC' | 'altI' | 'altD';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, CommonModule, ChangePasswordDrawerComponent, ImportDataPanelComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  isChangePasswordDrawerOpen = false;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  /** Two-key transaction shortcut: wait for second key (R, S, C, A) */
  private _pendingTransaction: PendingTransactionShortcut | null = null;
  private _pendingTransactionTimeout: ReturnType<typeof setTimeout> | null = null;
  private static readonly TRANSACTION_SECOND_KEY_MS = 1200;

  constructor(
    private _changePasswordService: ChangePasswordService,
    private keyboardNav: KeyboardNavigationService,
    private globalShortcuts: GlobalShortcutsService,
    private moduleDropdownShortcuts: ModuleDropdownShortcutService,
    private router: Router,
    private _workspaceTabs: WorkspaceTabsService,
    private _workspaceDialogs: WorkspaceDialogService,
    private _appDialogPositions: AppDialogPositionsService,
    private _commandPalette: CommandPaletteService,
    private _matDialog: MatDialog,
  ) {}

  private _detachDialogFocus?: () => void;

  /** Global shortcuts: workspace-tab-aware navigation (syncs highlighted tab with the page). */
  private _navigateByUrl(url: string): void {
    void this._workspaceTabs.navigateFromShell(url);
  }

  ngOnInit(): void {
    // Capture phase: Chrome handles Ctrl+K (address bar) before a bubbling document listener can cancel it.
    window.addEventListener('keydown', this._onCommandPaletteKeydown, true);
    window.addEventListener('keyup', this._onCommandPaletteKeyup, true);

    this._detachDialogFocus = attachDialogFocusManagement(this._matDialog);

    // Subscribe to change password drawer state
    this._changePasswordService.isDrawerOpen$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((isOpen) => {
        this.isChangePasswordDrawerOpen = isOpen;
      });
  }

  ngOnDestroy(): void {
    window.removeEventListener('keydown', this._onCommandPaletteKeydown, true);
    window.removeEventListener('keyup', this._onCommandPaletteKeyup, true);
    this._detachDialogFocus?.();
    this._clearPendingTransaction();
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Ctrl/Cmd+K is a Chrome omnibox shortcut. Cancel it on the window capture phase,
   * before the browser default action, then open the page palette.
   */
  private readonly _onCommandPaletteKeydown = (event: KeyboardEvent): void => {
    if (!this._isCommandPaletteChord(event)) {
      return;
    }
    if (!this.globalShortcuts.isAppLevelShortcutContextActive()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (event.repeat) {
      return;
    }
    this._clearPendingTransaction();
    this._commandPalette.open();
  };

  /** Keyup half of the same chord, so Chrome does not focus the address bar on release. */
  private readonly _onCommandPaletteKeyup = (event: KeyboardEvent): void => {
    if (!this._isCommandPaletteChord(event)) {
      return;
    }
    if (!this.globalShortcuts.isAppLevelShortcutContextActive()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
  };

  private _isCommandPaletteChord(event: KeyboardEvent): boolean {
    const keyCode = (event as KeyboardEvent & { keyCode?: number }).keyCode;
    return (
      (event.ctrlKey || event.metaKey) &&
      !event.altKey &&
      !event.shiftKey &&
      (event.key === 'k' || event.key === 'K' || event.code === 'KeyK' || keyCode === 75)
    );
  }

  /**
   * Clear pending two-key transaction shortcut and timeout
   */
  private _clearPendingTransaction(): void {
    if (this._pendingTransactionTimeout != null) {
      clearTimeout(this._pendingTransactionTimeout);
      this._pendingTransactionTimeout = null;
    }
    this._pendingTransaction = null;
  }

  /**
   * Handle second key of two-key transaction shortcut (e.g. R after Alt+P)
   * Returns true if event was handled.
   */
  private _handleTransactionSecondKey(event: KeyboardEvent): boolean {
    const key = (event.key || '').toLowerCase();
    const pending = this._pendingTransaction;
    this._clearPendingTransaction();

    if (pending === 'altP') {
      if (key === 'm') {
        event.preventDefault();
        this._navigateByUrl('/purchase/list');
        return true;
      }
      if (key === 'r') {
        event.preventDefault();
        this._navigateByUrl('/purchase-return/add');
        return true;
      }
      if (key === 's') {
        event.preventDefault();
        this._navigateByUrl('/payment-to-suppliers/add');
        return true;
      }
      event.preventDefault();
      this._navigateByUrl('/purchase/add');
      return true;
    }
    if (pending === 'altS') {
      if (key === 'm') {
        event.preventDefault();
        this._navigateByUrl('/sales/list');
        return true;
      }
      if (key === 'l') {
        event.preventDefault();
        this._navigateByUrl('/supplier/list');
        return true;
      }
      if (key === 'r') {
        event.preventDefault();
        this._navigateByUrl('/sales-return/add');
        return true;
      }
      if (key === 'a') {
        event.preventDefault();
        this._navigateByUrl('/stock-adjustment/add');
        return true;
      }
      event.preventDefault();
      this._navigateByUrl('/sales/add');
      return true;
    }
    if (pending === 'altC') {
      if (key === 'l') {
        event.preventDefault();
        this._navigateByUrl('/customers/list');
        return true;
      }
      if (key === 'r') {
        event.preventDefault();
        this._navigateByUrl('/customer-receipts/add');
        return true;
      }
      event.preventDefault();
      this._navigateByUrl('/customers/list');
      return true;
    }
    if (pending === 'altI') {
      if (key === 'l') {
        event.preventDefault();
        this._navigateByUrl('/item/list');
        return true;
      }
      event.preventDefault();
      this._navigateByUrl('/item/list');
      return true;
    }
    if (pending === 'altD') {
      if (key === 'l') {
        event.preventDefault();
        this._navigateByUrl('/doctor/list');
        return true;
      }
      event.preventDefault();
      this._navigateByUrl('/doctor/list');
      return true;
    }
    return false;
  }

  /**
   * Start pending transaction shortcut (Alt+P, Alt+S, or Alt+R) and schedule default navigation
   */
  private _startPendingTransaction(pending: PendingTransactionShortcut, event: KeyboardEvent): void {
    event.preventDefault();
    const tabId = this._workspaceTabs.activeTabId();
    if (tabId) {
      this._workspaceDialogs.suspendForTab(tabId);
    }
    this._pendingTransaction = pending;
    this._pendingTransactionTimeout = setTimeout(() => {
      this._pendingTransactionTimeout = null;
      this._pendingTransaction = null;
      if (pending === 'altP') {
        this._navigateByUrl('/purchase/add');
      } else if (pending === 'altS') {
        this._navigateByUrl('/sales/add');
      } else if (pending === 'altC') {
        this._navigateByUrl('/customers/list');
      } else if (pending === 'altI') {
        this._navigateByUrl('/item/list');
      } else if (pending === 'altD') {
        this._navigateByUrl('/doctor/list');
      }
    }, AppComponent.TRANSACTION_SECOND_KEY_MS);
  }

  /**
   * Handle drawer closed event
   */
  onChangePasswordDrawerClosed(): void {
    this._changePasswordService.closeDrawer();
  }

  /**
   * Global Enter key handler - triggers edit action when not in input field,
   * otherwise moves to next field
   */
  @HostListener('document:keydown.enter', ['$event'])
  onGlobalEnterKey(event: KeyboardEvent): void {
    if (!this.globalShortcuts.isAppLevelShortcutContextActive()) {
      return;
    }
    // An open dialog owns Enter, even when focus has fallen to the page body.
    if (hasVisibleDialogOverlay()) {
      return;
    }

    const target = event.target as HTMLElement;
    // Master forms use capture-phase MasterFormKeyNavDirective only — avoid double handling.
    if (target.closest?.('[appMasterFormKeyNav]')) {
      return;
    }
    // These screens own Enter (header order, grid cells, footer) in their page components.
    if (target.closest?.('app-sales-billing, app-sales-return-create, app-purchase-return-create, app-purchase-order')) {
      return;
    }
    if (target.closest?.('.cdk-overlay-container')) return;

    if (document.querySelector('.mat-mdc-select-panel, .mat-select-panel')) {
      return;
    }
    if (target.closest('mat-select, mat-mdc-select, .mat-mdc-select-trigger')) {
      return;
    }
    if (
      target.closest(
        '.master-list-filter-toolbar, .generic-list-filters, .item-list-toolbar-top-row',
      )
    ) {
      return;
    }

    // If user is typing in input/textarea/select, use navigation service
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT'
    ) {
      // Check if we should use navigation service (for form navigation)
      if (this.keyboardNav.handleGlobalEnterKey(event)) {
        return; // Navigation service handled it
      }
      // If navigation service didn't handle it, fall through to edit handler
    }

    // If not in input field (e.g., on list page), trigger edit action
    // This allows Enter to edit selected item when focus is on the page/list
    this.globalShortcuts.handleEnter(event);
  }

  /**
   * Block Firefox from activating its native menu bar on Alt key release.
   * Firefox triggers menu bar on keyup for the Alt key alone, which cannot
   * be prevented via keydown. This listener prevents that behavior.
   */
  @HostListener('document:keyup', ['$event'])
  onGlobalKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Alt') {
      event.preventDefault();
    }
  }

  /**
   * Electron main process swallows Ctrl/Cmd+K before the page sees it, then dispatches this event.
   */
  @HostListener('document:mediastra-command-palette')
  onCommandPaletteRequest(): void {
    if (!this.globalShortcuts.isAppLevelShortcutContextActive()) {
      return;
    }
    this._clearPendingTransaction();
    this._commandPalette.open();
  }

  /**
   * Global keydown: list shortcuts (Alt+I/C/S/D), transaction shortcut (Alt+P), Home, plus key
   */
  @HostListener('document:keydown', ['$event'])
  onGlobalKeyDown(event: KeyboardEvent): void {
    if (!this.globalShortcuts.isAppLevelShortcutContextActive()) {
      this._clearPendingTransaction();
      return;
    }

    // 0) F11: intercept browser fullscreen and use Fullscreen API instead (so Esc works)
    if (event.key === 'F11' || event.code === 'F11') {
      event.preventDefault();
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      } else if (document.fullscreenEnabled) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
      return;
    }

    // Ctrl/Cmd+K is handled in the window capture listener (_onCommandPaletteKeydown).

    // While a dialog is open, shortcuts below must not navigate, open menus or create behind it.
    if (hasVisibleDialogOverlay()) {
      this._clearPendingTransaction();
      return;
    }

    // 1) Two-key transaction shortcut: pending second key
    if (this._pendingTransaction != null) {
      if (this._handleTransactionSecondKey(event)) {
        return;
      }
    }

    // 2) Module dropdown shortcuts: Alt + M / T / R / A / U
    // Always work globally (including when focus is in input/textarea/select) so users can
    // open Masters/Transactions/etc. from any page (Sales add, lists, etc.) without blurring first.
    if (event.altKey) {
      const key = (event.key || '').toLowerCase();
      const moduleMap: Record<string, string> = {
        m: 'masters',
        t: 'transactions',
        r: 'reports',
        a: 'administration',
        u: 'utilities',
      };
      const moduleId = moduleMap[key];
      if (moduleId) {
        event.preventDefault();
        event.stopPropagation();
        this.moduleDropdownShortcuts.requestOpenDropdown(moduleId);
        return;
      }
    }

    // 3) List page shortcuts: Alt + I/C/D/S then L (two-key)
    // 4) Transaction shortcuts: Alt + P (Purchase), Alt + S (Sales)
    // All work globally (including when focus is in input) for consistent behavior on any page.
    if (event.altKey) {
      const key = (event.key || '').toLowerCase();
      if (key === 'i') {
        event.preventDefault();
        event.stopPropagation();
        this._startPendingTransaction('altI', event);
        return;
      }
      if (key === 'c') {
        event.preventDefault();
        event.stopPropagation();
        this._startPendingTransaction('altC', event);
        return;
      }
      if (key === 'd') {
        event.preventDefault();
        event.stopPropagation();
        this._startPendingTransaction('altD', event);
        return;
      }
      if (key === 'p') {
        event.preventDefault();
        event.stopPropagation();
        this._startPendingTransaction('altP', event);
        return;
      }
      if (key === 's') {
        event.preventDefault();
        event.stopPropagation();
        this._startPendingTransaction('altS', event);
        return;
      }
    }

    // 5) Home key → Navigate to Dashboard (always, from any page or field)
    if (event.key === 'Home' || event.code === 'Home' || event.keyCode === 36) {
      event.preventDefault();
      event.stopPropagation();
      this._navigateByUrl('/dashboard');
      return;
    }

    // 6) Plus key triggers create action
    const target = event.target as HTMLElement;
    if (target.closest?.('.cdk-overlay-container')) return;
    // Plus key triggers create action
    const isPlus =
      event.key === '+' || event.code === 'Equal' || event.keyCode === 187;
    if (isPlus) {
      this.globalShortcuts.handleCreate(event);
    }
  }
}
